import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

// THE CHAT ACCEPTANCE, in the World (Khari, 2026-10-19: *"On any user's initial
// chat with an avatar (per session), they must click a warning and acceptance
// screen with a checkbox. For members, this only happens once and we record it.
// I don't want to use a modal. Make the screen directly into the chat area,
// covering the chat log/UI."*). The website and the app have had it since that
// day; the World's chat docks had not — and the Living City (2026-10-23) puts
// the artists on the street where anyone can walk up and talk.
//
// SAME WORDS, SAME VERSION, SAME RECORD as the website's
// (Web src/components/chat/ChatAcceptance.jsx) and the app's. A member who
// accepted on the website is never asked here; one who accepts here is never
// asked there. ⚠ Changing the words means bumping CHAT_TERMS_VERSION in all
// three, together.
//
// Once per browser session (sessionStorage) for everyone; members once ever,
// remembered by the API (GET/POST /api/chat/acceptance, migration 411).

export const CHAT_TERMS_VERSION = '2026-10-19';
const TERMS_URL = 'https://www.hypetunes.com/terms';

const POINTS = [
  'Every artist you chat with on HypeTunes is an AI, even the ones made after a real person. It is never that person typing to you.',
  "AI can get things wrong. Don't rely on it for medical, legal, financial or safety advice.",
  "Keep private things private. Don't share passwords, payment details or anything you wouldn't want saved.",
  'Your chats are saved to your account so the artist can remember you.',
  'Be respectful. Abusive or harmful messages are not allowed.',
];

const scope = AUTH_TOKEN ? `u:${String(AUTH_TOKEN).slice(-24)}` : 'guest';
const key = `ht:chatAccepted:${CHAT_TERMS_VERSION}:${scope}`;
const readSession = () => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };

// One answer for the whole page: accepting in one chat covers every artist.
let state = { session: readSession(), server: 'unknown' };   // server: unknown | checking | remembered | not
const subs = new Set();
const set = (next) => { state = { ...state, ...next }; subs.forEach((f) => f()); };
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
const snap = () => state;

export function useChatAcceptance() {
  const s = useSyncExternalStore(subscribe, snap, snap);
  useEffect(() => {
    if (!AUTH_TOKEN || s.session || s.server !== 'unknown') return;
    set({ server: 'checking' });
    fetch(`${API_BASE}/api/chat/acceptance?version=${encodeURIComponent(CHAT_TERMS_VERSION)}`, {
      headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      // A failed read is "not remembered": asking again is the safe way to be wrong.
      .then((d) => set({ server: d?.remembered ? 'remembered' : 'not' }))
      .catch(() => set({ server: 'not' }));
  }, [s.session, s.server]);
  const accepted = s.session || s.server === 'remembered';
  const checking = !accepted && !!AUTH_TOKEN && (s.server === 'unknown' || s.server === 'checking');
  const accept = useCallback(() => {
    try { sessionStorage.setItem(key, '1'); } catch { /* the in-memory answer still holds */ }
    set({ session: true });
    if (!AUTH_TOKEN) return;
    fetch(`${API_BASE}/api/chat/acceptance`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${AUTH_TOKEN}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ version: CHAT_TERMS_VERSION, platform: 'web' }),
    }).catch(() => {});
  }, []);
  return { needed: !accepted, checking, accept };
}

const card = {
  width: 'min(380px, 88vw)', boxSizing: 'border-box', padding: '16px 18px 18px',
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 16,
  color: '#ffe9c4', font: '14px/1.45 system-ui, sans-serif',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: '0 16px 50px rgba(0,0,0,0.5)',
  pointerEvents: 'auto', maxHeight: 'min(80vh, 640px)', overflowY: 'auto',
};

/**
 * The screen itself — it takes the place of the chat until it is answered. At
 * the World's door (Loader) the same screen, the same words and the same record,
 * with its own heading and button: there it is the way in.
 */
export function ChatAcceptance({ checking, onAccept, onClose, style, eyebrow = 'Before you chat', title = "You're chatting with an AI", cta = 'Start chatting' }) {
  const [agreed, setAgreed] = useState(false);
  if (checking) {
    return <div style={{ ...card, ...style, textAlign: 'center', color: 'rgba(255,233,196,0.6)' }}>…</div>;
  }
  return (
    <div style={{ ...card, ...style }} role="region" aria-label={eyebrow}>
      <div style={{ color: '#ffb25e', fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase' }}>{eyebrow}</div>
      <div style={{ fontSize: 18, fontWeight: 800, margin: '4px 0 8px' }}>{title}</div>
      <ul style={{ margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: 6, color: 'rgba(255,233,196,0.8)', fontSize: 13.5 }}>
        {POINTS.map((p) => <li key={p}>{p}</li>)}
      </ul>
      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 12, cursor: 'pointer', fontSize: 13.5 }}>
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ width: 18, height: 18, marginTop: 2, accentColor: '#ffb25e', flex: 'none' }} />
        <span>I understand I&apos;m chatting with an AI, and I agree to the{' '}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#ffb25e', fontWeight: 700 }}>Terms of Service</a>.</span>
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button" disabled={!agreed} onClick={onAccept}
          style={{ flex: 1, padding: '11px 14px', border: 0, borderRadius: 999, background: '#ffb25e', color: '#1a1205', font: '800 14px system-ui', cursor: agreed ? 'pointer' : 'not-allowed', opacity: agreed ? 1 : 0.4 }}
        >{cta}</button>
        {onClose && (
          <button type="button" onClick={onClose} style={{ padding: '11px 14px', borderRadius: 999, background: 'transparent', color: '#ffe9c4', border: '1px solid rgba(255,178,94,0.35)', font: '600 13px system-ui', cursor: 'pointer' }}>Not now</button>
        )}
      </div>
    </div>
  );
}

export default ChatAcceptance;
